import { createServerFn } from "@tanstack/react-start";

// Gold vs US dollar and real yields, built from official public data:
// FRED DTWEXBGS (Fed Nominal Broad US Dollar Index), DFII10 (10Y TIPS real
// yield), DGS10 (10Y nominal Treasury yield), joined with our stored monthly
// XAUUSD closes. Short in-memory cache with last good fallback.

export type CorrPoint = { ym: string; gold: number; usd: number; real: number; nominal: number };
export type CorrWindow = { months: number; goldUsd: number | null; goldReal: number | null; n: number };
export type CorrelationData = {
  latest: { date: string; usd: number; real: number; nominal: number; breakeven: number } | null;
  windows: CorrWindow[];
  rolling: { ym: string; goldUsd: number | null; goldReal: number | null }[];
  series: CorrPoint[];
  asOf: string;
  stale: boolean;
};

const TTL = 6 * 60 * 60 * 1000;
let cache: { at: number; value: CorrelationData } | null = null;

function pearson(a: number[], b: number[]): number | null {
  const n = a.length;
  if (n < 6) return null;
  const ma = a.reduce((s, x) => s + x, 0) / n;
  const mb = b.reduce((s, x) => s + x, 0) / n;
  let cov = 0, va = 0, vb = 0;
  for (let i = 0; i < n; i++) {
    const da = a[i] - ma, db = b[i] - mb;
    cov += da * db; va += da * da; vb += db * db;
  }
  if (va === 0 || vb === 0) return null;
  return Math.round((cov / Math.sqrt(va * vb)) * 100) / 100;
}

async function readFred(): Promise<Map<string, { usd?: number; real?: number; nominal?: number; date: string }>> {
  const url = "https://fred.stlouisfed.org/graph/fredgraph.csv?id=DTWEXBGS,DFII10,DGS10&cosd=2014-01-01";
  const res = await fetch(url, { headers: { "User-Agent": "XAUProAcademy/1.0" } });
  if (!res.ok) throw new Error(`FRED ${res.status}`);
  const text = await res.text();
  const byMonth = new Map<string, { usd?: number; real?: number; nominal?: number; date: string }>();
  for (const line of text.trim().split("\n").slice(1)) {
    const [date, usd, real, nominal] = line.split(",");
    const ym = date.slice(0, 7);
    const row = byMonth.get(ym) ?? { date };
    const u = Number(usd), r = Number(real), n = Number(nominal);
    // Keep the last valid observation of each month (month-end reading).
    if (usd && Number.isFinite(u)) row.usd = u;
    if (real && Number.isFinite(r)) row.real = r;
    if (nominal && Number.isFinite(n)) row.nominal = n;
    row.date = date;
    byMonth.set(ym, row);
  }
  return byMonth;
}

async function build(): Promise<CorrelationData> {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const [fred, { data: gold, error }] = await Promise.all([
    readFred(),
    supabaseAdmin.from("gold_monthly_history").select("year,month,close").limit(1000),
  ]);
  if (error) throw new Error(error.message);
  const goldMap = new Map<string, number>();
  for (const g of gold ?? []) {
    if (g.close == null) continue;
    goldMap.set(`${g.year}-${String(g.month).padStart(2, "0")}`, Number(g.close));
  }
  const series: CorrPoint[] = [];
  for (const [ym, f] of [...fred.entries()].sort(([a], [b]) => a.localeCompare(b))) {
    const g = goldMap.get(ym);
    if (g == null || f.usd == null || f.real == null || f.nominal == null) continue;
    series.push({ ym, gold: g, usd: f.usd, real: f.real, nominal: f.nominal });
  }
  // Monthly changes: gold % and dollar %, real yield in basis points.
  const ch = series.slice(1).map((p, i) => ({
    ym: p.ym,
    g: (p.gold / series[i].gold - 1) * 100,
    u: (p.usd / series[i].usd - 1) * 100,
    r: (p.real - series[i].real) * 100,
  }));
  const win = (m: number): CorrWindow => {
    const s = ch.slice(-m);
    return { months: m, n: s.length, goldUsd: pearson(s.map((x) => x.g), s.map((x) => x.u)), goldReal: pearson(s.map((x) => x.g), s.map((x) => x.r)) };
  };
  const rolling = ch.map((x, i) => {
    if (i < 23) return { ym: x.ym, goldUsd: null, goldReal: null };
    const s = ch.slice(i - 23, i + 1);
    return { ym: x.ym, goldUsd: pearson(s.map((y) => y.g), s.map((y) => y.u)), goldReal: pearson(s.map((y) => y.g), s.map((y) => y.r)) };
  }).filter((r) => r.goldUsd != null);

  const last = [...fred.values()].reverse().find((f) => f.usd != null && f.real != null && f.nominal != null);
  return {
    latest: last ? { date: last.date, usd: last.usd!, real: last.real!, nominal: last.nominal!, breakeven: Math.round((last.nominal! - last.real!) * 100) / 100 } : null,
    windows: [win(12), win(36), win(120)],
    rolling,
    series: series.slice(-120),
    asOf: new Date().toISOString(),
    stale: false,
  };
}

export const getGoldCorrelation = createServerFn({ method: "GET" }).handler(async (): Promise<CorrelationData> => {
  if (cache && Date.now() - cache.at < TTL) return cache.value;
  try {
    const value = await build();
    cache = { at: Date.now(), value };
    return value;
  } catch (e) {
    console.error("[getGoldCorrelation]", e instanceof Error ? e.message : e);
    if (cache) return { ...cache.value, stale: true };
    return { latest: null, windows: [], rolling: [], series: [], asOf: new Date().toISOString(), stale: true };
  }
});
